import { useState, useCallback } from 'react';
import type { EmailTemplate, VariableStatus } from '../types';

interface UseTemplateValidationResult {
  variableStatus: VariableStatus[];
  hasValidationIssues: boolean;
  validateTemplate: (template: EmailTemplate, leadCount: number) => Promise<{
    statuses: VariableStatus[];
    hasIssues: boolean;
  }>;
  getMissingVars: () => VariableStatus[];
  clearValidation: () => void;
}

interface Column {
  key: string;
  name: string;
}

/**
 * Hook for validating template variables against dataset columns.
 * Extracts {{variable}} patterns and checks if columns exist.
 */
export function useTemplateValidation(columns: Column[]): UseTemplateValidationResult {
  const [variableStatus, setVariableStatus] = useState<VariableStatus[]>([]);
  const [hasValidationIssues, setHasValidationIssues] = useState(false);

  /**
   * Extract variables from template text ({{variable}} pattern)
   */
  const extractVariables = useCallback((text: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      const variable = match[1].trim().toLowerCase();
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    return variables;
  }, []);

  /**
   * Validate template variables against columns
   */
  const validateTemplate = useCallback(
    async (
      template: EmailTemplate,
      leadCount: number
    ): Promise<{ statuses: VariableStatus[]; hasIssues: boolean }> => {
      const allText = `${template.subject} ${template.body}`;
      const variables = extractVariables(allText);

      // Build column lookup map (both key and name, case-insensitive)
      const columnMap: Record<string, string> = {};
      columns.forEach((col) => {
        columnMap[col.key.toLowerCase()] = col.key;
        columnMap[col.name.toLowerCase()] = col.key;
      });

      // Check each variable
      const statuses: VariableStatus[] = variables.map((variable) => {
        const columnKey = columnMap[variable];
        const columnExists = !!columnKey;

        return {
          variable,
          columnExists,
          columnName: columnKey,
          populatedCount: columnExists ? leadCount : 0,
          totalCount: leadCount,
          status: columnExists ? ('ok' as const) : ('missing' as const),
        };
      });

      const hasIssues = statuses.some((s) => s.status !== 'ok');

      setVariableStatus(statuses);
      setHasValidationIssues(hasIssues);

      return { statuses, hasIssues };
    },
    [columns, extractVariables]
  );

  /**
   * Get only variables with issues
   */
  const getMissingVars = useCallback((): VariableStatus[] => {
    return variableStatus.filter((v) => v.status !== 'ok');
  }, [variableStatus]);

  /**
   * Clear validation state
   */
  const clearValidation = useCallback(() => {
    setVariableStatus([]);
    setHasValidationIssues(false);
  }, []);

  return {
    variableStatus,
    hasValidationIssues,
    validateTemplate,
    getMissingVars,
    clearValidation,
  };
}
